import { FC, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Box } from '@mrshmllw/smores-react'

import { ErrorComponent } from '../ErrorComponent'
import { ReferralCodeError } from '../ErrorComponent/ReferralCodeError'

import { Storage as storage, useEventTracking, useTracking } from 'utils'

type ErrorProps = {
  refresh?: boolean
}

export const ErrorPage: FC<ErrorProps> = ({ refresh }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { quoteId } = useParams<{ quoteId: string }>()
  const { directFieldsError, referralCodeError } = location.state ?? false
  const track = useEventTracking()

  const handleStartAgain = () => {
    track('Error page - Start quote button clicked')
    const existingQuoteId = storage.get('quoteId')
    const quoteIdToUse = quoteId || existingQuoteId
    const quoteHasDirectData = Boolean(storage.get(`${quoteIdToUse}_DIRECT`))
    const isComparatorQuote = Boolean(storage.get('comparator_quotes')?.includes(quoteIdToUse))
    const isQuoteCompleted = storage.get(`quote-${quoteIdToUse}-completed`)

    if (isQuoteCompleted || isComparatorQuote) {
      if (location.pathname.includes('overview')) {
        return window.location.reload()
      }
      return navigate(`/quote/${quoteIdToUse}/overview`)
    }

    if (quoteHasDirectData && !isQuoteCompleted) {
      return navigate(`/direct/${quoteIdToUse}/start`)
    }

    return navigate('/start-quote/')
  }

  const refreshPage = () => {
    track('Error page - Refresh page button clicked')
    window.location.reload()
  }

  const goBack = () => {
    track('Error page - Go back clicked')
    navigate(-1)
  }

  useTracking('Error')

  useEffect(() => {
    const previousPage = JSON.stringify(location.state)
    track('Error redirected from' + previousPage)
  }, [location.state])

  return (
    <Box flex direction="column" alignItems="center">
      {referralCodeError ? (
        <ReferralCodeError />
      ) : (
        <ErrorComponent
          description={
            directFieldsError
              ? "Looks like we've hit a technical glitch. If you can, give us some time to fix it then come back and click refresh. This may take us a day or so."
              : "Looks like we've hit a technical glitch, how embarrassing! Click the refresh button to reload the page. If that doesn't fix it, please start again."
          }
          handlePrimaryButtonClick={refresh ? refreshPage : goBack}
          handleSecondaryButtonClick={directFieldsError ? undefined : handleStartAgain}
          secondaryButtonLabel={directFieldsError ? undefined : 'Start again'}
          primaryButtonLabel="Try refresh"
          title="Oops, something broke"
        />
      )}
    </Box>
  )
}
